import { Component, Inject, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SharingPatientId } from '../sharing-setup/sharing-setup.component';
import { PatientOverviewFacade } from './patient-overview.facade';
import { PatientOverviewData } from './patient-overview.models';
import { FacilityUserSelectionMockComponent } from '../facility-user-selection-mock/facility-user-selection-mock.component';
import { FacilityInfo, FacilitatorInfo } from '../transfer-visualization/transfer-visualization.component';

@Component({
    selector: 'app-patient-overview',
    templateUrl: './patient-overview.component.html',
    styleUrls: ['./patient-overview.component.scss'],
    providers: [PatientOverviewFacade],
    standalone: false
})
export class PatientOverviewComponent {
  @ViewChild('imageShareRight', { static: false }) imageShareRight!: ElementRef;
  private selectedOption: string | null = null;
  selectedFacility?: FacilityInfo;
  selectedFacilitator?: FacilitatorInfo;
  exchangeMode: 'send' | 'receive' | null = null;

  constructor(
    public dialogRef: MatDialogRef<PatientOverviewComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: PatientOverviewData,
    private facade: PatientOverviewFacade,
    private router: Router,
    public dialog: MatDialog
  ) {
    this.facade.initialize(this.dialogRef, data);
  }

  onSelectedPatientIdEvent(patientId: SharingPatientId): void {
    this.facade.onSelectedPatientIdEvent(patientId);
  }

  onSharingFacilitySelected(patientId: SharingPatientId): void {
    this.facade.onSharingFacilitySelected(patientId);
  }

  onShareWithFacility(): void {
    this.facade.onShareWithFacility();
  }

  onShareFromFacility(): void {
    this.facade.onShareFromFacility();
  }

  onSharingGoNext(): void {
    this.facade.onSharingGoNext();
    this.cdr.detectChanges();
  }

  onClose(): void {
    this.facade.onClose();
    this.router.navigateByUrl('/');
  }

  onValueChange($event: 'send' | 'receive' | null) {
    console.log('onValueChange', $event);
    this.exchangeMode = $event;
    
    // If no value selected, clear the visualization
    if (!$event) {
      this.selectedFacility = undefined;
      this.selectedFacilitator = undefined;
      this.cdr.markForCheck();
      return;
    }
    
    if (this.selectedOption === 'Images') {
      if ($event === 'send') {
        this.openFacilitySelectionDialog(() => this.facade.goToFullscreenMode('pacs'));
      } else {
        this.facade.goToFullscreenMode('nuage');
      }
      return;
    }

    // When "Documents" is selected, trigger the document share flow for both SEND and RECEIVE
    if (this.selectedOption === 'Documents') {
      this.onDocumentShare();
      return;
    }

    // If SEND open a mat dialog with the Facility User Selection Mock Component inside it
    if ($event === 'send') {
      this.openFacilitySelectionDialog();
    }
  }

  private openFacilitySelectionDialog(onSelectionConfirmed?: () => void): void {
    const dialogRef = this.dialog.open(FacilityUserSelectionMockComponent, {
      width: '640px',
      maxWidth: '92vw',
      panelClass: 'facility-selection-dialog-panel',
      autoFocus: false,
      restoreFocus: true,
      data: {
        patientId: this.vm.currentFacilityPatientId
      }
    });

    const componentInstance = dialogRef.componentInstance;
    if (componentInstance) {
      componentInstance.selectionConfirmed.subscribe((result: {
        facility: any;
        facilitator: any;
      }) => {
        if (result.facility && result.facilitator) {
          this.selectedFacility = {
            name: result.facility.name,
            patientId: result.facility.patientId
          };
          this.selectedFacilitator = {
            email: result.facilitator.email,
            name: result.facilitator.name
          };
          this.cdr.markForCheck();
          onSelectionConfirmed?.();
        }
      });
    }
  }

  onOptionChange(option: string) {
    console.log('onOptionChange', option);
    this.selectedOption = option;
    // Clear visualization when option changes
    this.selectedFacility = undefined;
    this.selectedFacilitator = undefined;
    this.exchangeMode = null;
    this.cdr.markForCheck();
  }

  get sourceFacility(): FacilityInfo | undefined {
    if (!this.vm.currentFacilityPatientId) {
      return undefined;
    }
    // Get facility name from current facility ID
    // For now, we'll use a formatted version of the facility ID
    // In a real implementation, you'd fetch the facility name from a service
    const facilityName = this.vm.currentFacility || 'Current Facility';
    return {
      name: facilityName,
      patientId: this.vm.currentFacilityPatientId.value
    };
  }

  async onDocumentShare(): Promise<void> {
    return this.facade.onDocumentShare();
  }

  onImageShare(): void {
    this.facade.onImageShare();
  }

  onPageChange(page: number): void {
    this.facade.onPageChange(page);
  }

  onRestartSharingSetup(): void {
    this.facade.onRestartSharingSetup();
    this.cdr.detectChanges();
  }

  onIframeLoaded(): void {
    this.facade.onIframeLoaded();
  }

  get activeImageShareTitle(): string {
    return this.facade.activeImageShareTitle;
  }

  get vm(): PatientOverviewFacade {
    return this.facade;
  }
}
